import React from 'react'
import Card from '../../../common/Card'
import { capitalizeFirstLetter } from '../../../../consts'
import {
  isAssignedPcp,
  isIndividualProvider,
  isAntidoteProvider,
  getProviderSpecialities
} from 'utils/providerSearchUtils'
import $t from '../../../../i18n'
import dayjs from 'dayjs'
import { Button } from 'antd'
import ProviderTier from '../../../common/ProviderTier/ProviderTier'
import ProviderCopay from '../ProviderCopay/ProviderCopay'
import ChangePcpContainer from '../ChangePcpContainer/ChangePcpContainer'

import providerAvatar from 'resources/generic-doctor.svg'
import buildings from 'resources/buildings.png'

import './ProviderItem.css'

const ProviderItem = ({
  provider,
  onClick,
  onAdditionalActionClick,
  providerTypeHeader,
  shouldShowDistance = true,
  isLoggedIn = false,
  pcpFamilyOwnership = false,
  isChangePcpFlow = false,
  onChangePcpSelection,
  onConfirmPcpSelection,
  changePcpSelectedProviderId,
  planDetailsCopay
}) => {
  const isLocationExist =
    provider?.address || provider?.city || provider?.state || provider?.zipcode
  const availableFormat = 'MM/DD hh:mm A'
  const isCareCoordinator = provider?.care_coordinator
  const phoneNumber = provider?.provider_numbers?.find(
    number => number?.number_type === 'phone'
  )?.number

  const isProviderIndividual = isIndividualProvider(provider)
  const isProviderAntidote = isAntidoteProvider(provider)

  const getProviderPcpOwnership = provider => {
    const isTelehealthPcp = provider.is_assigned_th_pcp
    const isPhysicalPcp = provider.is_assigned_physical_pcp

    const ownership = pcpFamilyOwnership
      ? $t('providerPage.providerDetails.pcpOwnership.yourFamily')
      : $t('providerPage.providerDetails.pcpOwnership.your')

    if (isTelehealthPcp) {
      return `${ownership} ${$t(
        'providerPage.providerDetails.pcpOwnership.telehealthPcp'
      )}`
    } else if (isPhysicalPcp) {
      return `${ownership} ${$t(
        'providerPage.providerDetails.pcpOwnership.officeVisitPcp'
      )}`
    }

    return ''
  }

  const shouldShowPcpSelectionSection = () => {
    return (
      isLoggedIn &&
      isChangePcpFlow &&
      isProviderIndividual &&
      !isAssignedPcp(provider)
    )
  }
  return (
    <>
      <Card className='provider-item'>
        <div className='provider-item-container'>
          {shouldShowPcpSelectionSection() && (
            <div className='change-pcp-flow-section-container'>
              <ChangePcpContainer
                provider={provider}
                onChangePcpSelection={onChangePcpSelection}
                onConfirmPcpSelection={onConfirmPcpSelection}
                changePcpSelectedProviderId={changePcpSelectedProviderId}
              />
            </div>
          )}
          <div className='provider-item-content' onClick={onClick}>
            <div className='provider-title-container'>
              <img
                src={isProviderIndividual ? providerAvatar : buildings}
                className='provider-img'
                alt=''
              />
              <div className='provider-title'>
                {providerTypeHeader && (
                  <div className='provider-header grey tiny'>
                    {providerTypeHeader}:
                  </div>
                )}
                <div className='semi-bold'>
                  {provider?.prac_category_code
                    ? provider?.name + ', ' + provider?.prac_category_code
                    : provider?.name}
                </div>
                <div className='provider-desc grey'>
                  {getProviderSpecialities(provider)}
                </div>
                {isCareCoordinator && (
                  <div className='provider-desc grey'>{provider?.email}</div>
                )}
                {isAssignedPcp(provider) && (
                  <div className='pcp-assigned-label'>
                    {provider.is_assigned_th_pcp && <i className='icon-star' />}
                    {getProviderPcpOwnership(provider)}
                  </div>
                )}
                {!isAssignedPcp(provider) &&
                  isProviderIndividual &&
                  provider?.accept_new_patients && (
                    <div className='accepting-new-members-label'>
                      <i className='icon-providers-accept' />
                      {$t('providerPage.providerDetails.newMembers')}
                    </div>
                  )}
              </div>
            </div>
            <div className='footer-mobile-position'>
              {provider?.tier && (
                <ProviderTier
                  tier={provider.tier}
                  textSize='small'
                  withBackground={true}
                />
              )}
            </div>
            <div className='provider-details'>
              {isProviderIndividual && (
                <>
                  <div className='provider-details-row'>
                    <i className='icon-profile' />
                    <div className='provider-desc'>
                      <span className='semi-bold'>
                        {provider?.gender
                          ? capitalizeFirstLetter(provider?.gender)
                          : $t('searchProviders.dataNotProvided')}
                      </span>
                    </div>
                  </div>
                  <div className='provider-details-row'>
                    <i className='icon-language' />
                    <div className='provider-desc'>
                      <span className='semi-bold'>
                        {$t('providerPage.providerDetails.speaks')}:{' '}
                      </span>
                      {provider?.languages?.length > 0
                        ? provider?.languages
                            ?.map(language => language?.name)
                            ?.join(', ')
                        : $t('searchProviders.dataNotProvided')}
                    </div>
                  </div>
                </>
              )}
              {!provider?.is_antidote && (
                <div className='provider-details-row'>
                  <i className='icon-phone' />
                  <div className='provider-desc'>
                    {phoneNumber || $t('searchProviders.dataNotProvided')}
                  </div>
                </div>
              )}
              {!!provider?.telehealth && provider?.next_availablility && (
                <div className='provider-details-row'>
                  <i className='icon-calendar' />
                  <div className='provider-desc'>
                    <span className='semi-bold'>
                      {$t('providerPage.providerDetails.nextAvailability')}:{' '}
                    </span>{' '}
                    {dayjs(provider?.next_availablility).format(
                      availableFormat
                    )}
                  </div>
                </div>
              )}
              {provider?.telehealth && (
                <div className='provider-details-row'>
                  <i className='icon-video' />
                  <div className='provider-desc semi-bold'>
                    {$t('providerPage.providerDetails.telehealth')}
                  </div>
                </div>
              )}
              {!provider?.is_antidote && (
                <div className='provider-extra-container'>
                  <div className='provider-details-row'>
                    <i className='icon-location' />
                    <div className='provider-desc'>
                      <div>
                        {isLocationExist
                          ? `${provider?.address || ''} ${[
                              provider?.city,
                              provider?.state,
                              provider?.zipcode
                            ]
                              .filter(val => !!val)
                              .join(', ')}`.trim()
                          : $t('searchProviders.dataNotProvided')}
                      </div>
                    </div>
                  </div>
                  {shouldShowDistance && provider?.distance_miles && (
                    <div className='provider-location'>
                      <i className='icon-Location' />
                      <div className='provider-location-text semi-bold provider-desc'>
                        {provider?.distance_miles?.toFixed(1) + ' mi'}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='provider-footer'>
          <div className='footer-desktop-position'>
            {provider?.tier && (
              <ProviderTier
                tier={provider.tier}
                textSize='small'
                withBackground={true}
              />
            )}
          </div>
          {isProviderAntidote ? (
            <div className='additional-action'>
              <span>
                {$t('searchProviders.ahProviderScheduleUnauthenticated')}
              </span>
              <Button
                className='black-cta'
                onClick={() => onAdditionalActionClick()}
              >
                {isLoggedIn
                  ? $t('searchProviders.scheduleVirtualVisit')
                  : $t('header.logIn')}
              </Button>
            </div>
          ) : isProviderIndividual ? (
            <ProviderCopay
              planDetailsCopay={planDetailsCopay}
              provider={provider}
              tooltipColor='var(--black)'
            />
          ) : null}
        </div>
      </Card>
    </>
  )
}

export default ProviderItem
